import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, ShieldCheck, Image as ImageIcon, Link as LinkIcon, Copy, QrCode, Key, Money, Translate, IdentificationCard, Signature as SignatureIcon } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import { humanizeAxiosError } from "@/lib/api";
import SignatureCapture from "@/components/SignatureCapture";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { user, refresh } = useAuth();
  const [twoFA, setTwoFA] = useState(!!user?.two_factor_enabled);
  const [phone, setPhone] = useState(user?.phone || "");
  const [photo, setPhoto] = useState(null);
  const [loginBg, setLoginBg] = useState(null);
  const [currentBg, setCurrentBg] = useState("");
  const [tagline, setTagline] = useState({ eyebrow: "", title: "", subtitle: "", eyebrow_en: "", title_en: "", subtitle_en: "" });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "" });
  const [loginHistory, setLoginHistory] = useState([]);
  useEffect(() => {
    api.get("/auth/login-history").then((r) => setLoginHistory(r.data || [])).catch(() => {});
  }, []);

  const pwChecks = React.useMemo(() => {
    const pw = passwordForm.new_password;
    if (!pw) return null;
    const checks = {
      length: pw.length >= 8,
      upper: /[A-ZĞÜŞİÖÇ]/.test(pw),
      lower: /[a-zğüşıöç]/.test(pw),
      number: /\d/.test(pw),
      noRepeat: !/(.)\1\1/.test(pw),
      noSequence: true,
    };
    const pwl = pw.toLowerCase();
    for (let i = 0; i < pwl.length - 2; i++) {
      const a = pwl.charCodeAt(i), b = pwl.charCodeAt(i + 1), c = pwl.charCodeAt(i + 2);
      if (((b - a === 1 && c - b === 1) || (a - b === 1 && b - c === 1)) && /[a-z0-9]/.test(pwl[i]) && /[a-z0-9]/.test(pwl[i+1]) && /[a-z0-9]/.test(pwl[i+2])) {
        checks.noSequence = false; break;
      }
    }
    return checks;
  }, [passwordForm.new_password]);
  const [salaryReq, setSalaryReq] = useState(user?.salary_pending || user?.salary || 0);
  const [stampFile, setStampFile] = useState(null);
  const [stamp, setStamp] = useState(null);
  const [autoStamp, setAutoStamp] = useState(false);
  const [nameReq, setNameReq] = useState({ first_name: "", last_name: "", reason: "" });
  const [newSig, setNewSig] = useState("");
  const [sigPending, setSigPending] = useState(null); // kendi bekleyen talebim
  const [sigRequests, setSigRequests] = useState(null); // onaycıysam bekleyen talepler (null=yetkisiz)
  const [profileForm, setProfileForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    tc_kimlik: user?.tc_kimlik || "",
    phone: user?.phone || "",
    department: user?.department || "",
    position: user?.position || "",
  });
  const { lang, setLang, t } = useI18n();
  const appUrl = typeof window !== "undefined" ? window.location.origin : "";
  const qrSrc = appUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=8&data=${encodeURIComponent(appUrl + "/login")}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(appUrl + "/login");
      toast.success("Bağlantı kopyalandı");
    } catch {
      toast.error("Kopyalama başarısız. Elle seçip kopyalayın.");
    }
  };

  React.useEffect(() => {
    if (user?.role === "admin") {
      api.get("/settings/login-image").then((r) => {
        const u = r.data.url;
        setCurrentBg(u.startsWith("http") ? u : `${API}${u}`);
      }).catch(() => {});
      api.get("/settings/login-tagline").then((r) => setTagline(r.data)).catch(() => {});
    }
    if (["admin", "hr", "gm"].includes(user?.role)) {
      api.get("/settings/company-stamp").then((r) => setStamp(r.data)).catch(() => {});
      api.get("/settings/auto-stamp").then((r) => setAutoStamp(!!r.data?.enabled)).catch(() => {});
    }
  }, [user]);

  const toggleAutoStamp = async (v) => {
    try {
      await api.post("/settings/auto-stamp", { enabled: !!v });
      setAutoStamp(!!v);
      toast.success(v ? "Otomatik kaşe açık" : "Otomatik kaşe kapalı");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const uploadStamp = async () => {
    if (!stampFile) return;
    const fd = new FormData(); fd.append("file", stampFile);
    try {
      const { data } = await api.post("/settings/company-stamp", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStamp({ ...(stamp || {}), url: data.url });
      setStampFile(null);
      toast.success("Şirket kaşesi güncellendi");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const savePref = async () => {
    await api.patch(`/personnel/${user.id}`, { two_factor_enabled: twoFA, phone });
    refresh(); toast.success("Kaydedildi");
  };

  const uploadPhoto = async () => {
    if (!photo) return;
    const fd = new FormData(); fd.append("file", photo);
    await api.post(`/personnel/${user.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    refresh(); toast.success("Fotoğraf yüklendi");
  };

  const uploadLoginBg = async () => {
    if (!loginBg) return;
    const fd = new FormData(); fd.append("file", loginBg);
    const { data } = await api.post(`/settings/login-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setCurrentBg(data.url.startsWith("http") ? data.url : `${API}${data.url}`);
    setLoginBg(null);
    toast.success("Giriş ekranı görseli güncellendi");
  };

  const saveTagline = async () => {
    await api.post("/settings/login-tagline", tagline);
    toast.success("Giriş metinleri güncellendi");
  };

  const changePassword = async () => {
    if (!passwordForm.current_password || passwordForm.new_password.length < 8) {
      return toast.error("Mevcut şifre ve en az 8 karakterlik yeni şifre gerekli");
    }
    if (pwChecks && !Object.values(pwChecks).every(Boolean)) {
      return toast.error("Yeni şifre güvenlik kurallarına uymuyor");
    }
    try {
      await api.post("/auth/change-password", passwordForm);
      toast.success("Şifreniz güncellendi");
      setPasswordForm({ current_password: "", new_password: "" });
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const requestSalaryChange = async () => {
    try {
      await api.post(`/personnel/${user.id}/salary-request`, { salary: Number(salaryReq) });
      toast.success("Maaş talebiniz İK/Admin onayına gönderildi");
      refresh();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const submitNameChange = async () => {
    if (!nameReq.first_name && !nameReq.last_name) {
      return toast.error("Yeni ad veya soyaddan en az birini girin");
    }
    try {
      await api.post("/personnel/name-change-request", nameReq);
      toast.success("İsim değişiklik talebiniz İK/Admin onayına gönderildi");
      setNameReq({ first_name: "", last_name: "", reason: "" });
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  React.useEffect(() => {
    setProfileForm({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      tc_kimlik: user?.tc_kimlik || "",
      phone: user?.phone || "",
      department: user?.department || "",
      position: user?.position || "",
    });
  }, [user?.id]);

  const saveProfile = async () => {
    if (!profileForm.first_name || !profileForm.last_name) {
      return toast.error(lang === "en" ? "First and last name are required" : "Ad ve soyad zorunlu");
    }
    try {
      await api.patch(`/personnel/${user.id}`, profileForm);
      toast.success(t("settings.profile_saved"));
      refresh();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const loadSigState = React.useCallback(() => {
    api.get("/signature-change-requests/mine").then((r) => setSigPending(r.data?.pending ? r.data : null)).catch(() => {});
    api.get("/signature-change-requests").then((r) => setSigRequests(r.data || [])).catch(() => setSigRequests(null));
  }, []);

  React.useEffect(() => { loadSigState(); }, [loadSigState]);

  const saveSignature = async () => {
    if (!newSig) return toast.error("Önce imzanızı ekleyin");
    try {
      await api.patch(`/personnel/${user.id}`, { signature: newSig });
      toast.success("İmzanız kaydedildi");
      setNewSig("");
      refresh();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const requestSignatureChange = async () => {
    if (!newSig) return toast.error("Önce yeni imzanızı ekleyin");
    try {
      const { data } = await api.post("/signature-change-requests", { signature: newSig });
      setNewSig("");
      if (data.direct) { toast.success("İmzanız güncellendi"); refresh(); }
      else { toast.success("İmza değişiklik talebiniz onaya gönderildi"); }
      loadSigState();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const decideSigRequest = async (rid, action) => {
    try {
      if (action === "reject") {
        const reason = window.prompt("Ret sebebi (opsiyonel):") || "";
        await api.post(`/signature-change-requests/${rid}/reject`, { reason });
        toast.success("Talep reddedildi");
      } else {
        await api.post(`/signature-change-requests/${rid}/approve`);
        toast.success("İmza değişikliği onaylandı");
      }
      loadSigState();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const backup = (fmt = "xlsx") => {
    const path = fmt === "xlsx" ? "/api/backup/xlsx" : "/api/backup";
    const ext = fmt === "xlsx" ? "xlsx" : "zip";
    fetch(`${API}${path}`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error("Yedekleme başarısız");
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `colossae_backup_${Date.now()}.${ext}`; a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(e.message));
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("settings.eyebrow")}</div>
        <h1 className="font-display text-3xl font-bold mt-1">{t("settings.title")}</h1>
      </div>

      {/* Profile Info — prominent for OAuth users with incomplete profile */}
      <Card data-testid="profile-info-card" className={(!user?.tc_kimlik || !user?.phone || user?.department === "-" || user?.position === "-") ? "border-primary/50 bg-primary/5" : ""}>
        <CardContent className="p-6 space-y-4">
          <div className="font-display font-bold text-lg flex items-center gap-2">
            <IdentificationCard size={20} /> {t("settings.profile_info")}
          </div>
          <div className="text-sm text-muted-foreground">{t("settings.profile_info_desc")}</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>{t("common.name")}</Label><Input value={profileForm.first_name} onChange={(e) => setProfileForm({...profileForm, first_name: e.target.value})} data-testid="profile-first-name" /></div>
            <div><Label>{t("common.surname")}</Label><Input value={profileForm.last_name} onChange={(e) => setProfileForm({...profileForm, last_name: e.target.value})} data-testid="profile-last-name" /></div>
            <div><Label>T.C. {lang === "en" ? "National ID" : "Kimlik No"}</Label><Input maxLength={11} value={profileForm.tc_kimlik} onChange={(e) => setProfileForm({...profileForm, tc_kimlik: e.target.value.replace(/\D/g, "")})} data-testid="profile-tc" placeholder="11 haneli" /></div>
            <div><Label>{t("common.phone")}</Label><Input value={profileForm.phone} onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})} data-testid="profile-phone" placeholder="05XX XXX XX XX" /></div>
            <div><Label>{t("common.department")}</Label><Input value={profileForm.department} onChange={(e) => setProfileForm({...profileForm, department: e.target.value})} data-testid="profile-department" placeholder={lang === "en" ? "e.g. Front Office" : "örn. Ön Büro"} /></div>
            <div><Label>{t("common.position")}</Label><Input value={profileForm.position} onChange={(e) => setProfileForm({...profileForm, position: e.target.value})} data-testid="profile-position" placeholder={lang === "en" ? "e.g. Receptionist" : "örn. Resepsiyonist"} /></div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveProfile} data-testid="profile-save">{t("settings.save_profile")}</Button>
          </div>
        </CardContent>
      </Card>

      {!user?.signature && (
        <Card className="border-warning bg-warning/10" data-testid="sig-required-card">
          <CardContent className="p-6 space-y-3">
            <div className="font-display font-bold text-lg flex items-center gap-2 text-warning-foreground"><SignatureIcon size={22} />{t("settings.signature_missing_title")}</div>
            <div className="text-sm">
              {t("settings.signature_missing_desc")}
              <div className="mt-1 text-xs text-muted-foreground">Not: İmzanızı daha sonra değiştirmek isterseniz yönetici (admin) onayı gerekir.</div>
            </div>
            <SignatureCapture onChange={setNewSig} testId="onboard-sig" autoUseSaved={false} />
            <div className="flex justify-end"><Button onClick={saveSignature} disabled={!newSig} data-testid="onboard-sig-save">{t("settings.save_signature")}</Button></div>
          </CardContent>
        </Card>
      )}

      {user?.signature && (
        <Card>
          <CardContent className="p-6 space-y-3">
            <div className="font-display font-bold text-lg flex items-center gap-2"><SignatureIcon size={20} />{t("settings.my_signature")}</div>
            <div className="text-sm text-muted-foreground">{t("settings.my_signature_desc")}</div>
            <div className="rounded-md border border-dashed border-border p-4 max-w-sm flex items-center justify-center bg-[repeating-conic-gradient(#f4f4f5_0deg_25%,#ffffff_0deg_50%)] bg-[length:14px_14px]">
              <img src={user.signature} alt="signature" className="h-24 object-contain" data-testid="current-sig" />
            </div>
            {sigPending ? (
              <div className="flex items-center gap-2 text-sm rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800 px-3 py-2 text-amber-800 dark:text-amber-300" data-testid="sig-pending-note">
                <SignatureIcon size={16} />
                İmza değişiklik talebiniz onay bekliyor. Onaylanınca yeni imzanız geçerli olacaktır.
              </div>
            ) : (
              <>
                <div className="text-xs text-muted-foreground">İmzanızı değiştirmek için yeni imzanızı çizip/yükleyip <b>onaya gönderin</b>. Değişiklik, admin (veya admin'in yetki verdiği kişi) onayından sonra geçerli olur.</div>
                <SignatureCapture onChange={setNewSig} testId="update-sig" autoUseSaved={false} />
                <div className="flex justify-end"><Button onClick={requestSignatureChange} disabled={!newSig} variant="outline" data-testid="sig-change-request">İmza Değişikliği Talep Et</Button></div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {Array.isArray(sigRequests) && sigRequests.length > 0 && (
        <Card className="border-primary/40" data-testid="sig-approvals-card">
          <CardContent className="p-6 space-y-3">
            <div className="font-display font-bold text-lg flex items-center gap-2"><SignatureIcon size={20} />İmza Değişiklik Talepleri ({sigRequests.length})</div>
            <div className="text-sm text-muted-foreground">Personelin gönderdiği imza değişikliklerini onaylayın veya reddedin.</div>
            <div className="space-y-2">
              {sigRequests.map((r) => (
                <div key={r.id} className="flex items-center gap-3 rounded-md border border-border p-3" data-testid={`sig-req-${r.id}`}>
                  <div className="rounded-md border border-dashed border-border p-2 bg-[repeating-conic-gradient(#f4f4f5_0deg_25%,#ffffff_0deg_50%)] bg-[length:12px_12px]">
                    <img src={r.new_signature} alt="yeni imza" className="h-16 w-32 object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.user_name}</div>
                    <div className="text-[11px] text-muted-foreground">{r.department || "-"} · {new Date(r.created_at).toLocaleString("tr-TR")}</div>
                  </div>
                  <Button size="sm" onClick={() => decideSigRequest(r.id, "approve")} data-testid={`sig-approve-${r.id}`}>Onayla</Button>
                  <Button size="sm" variant="destructive" onClick={() => decideSigRequest(r.id, "reject")} data-testid={`sig-reject-${r.id}`}>Reddet</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="font-display font-bold text-lg flex items-center gap-2"><Key size={20} /> {t("settings.change_password")}</div>
            <div><Label>{t("settings.current_password")}</Label><Input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})} data-testid="pw-current" /></div>
            <div><Label>{t("settings.new_password")}</Label><Input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})} data-testid="pw-new" /></div>
            {pwChecks && (
              <div className="rounded-md border border-border bg-muted/30 p-3 space-y-1" data-testid="pw-rules">
                {[
                  { key: "length", label: t("reg.pw_length") },
                  { key: "upper", label: t("reg.pw_upper") },
                  { key: "lower", label: t("reg.pw_lower") },
                  { key: "number", label: t("reg.pw_number") },
                  { key: "noRepeat", label: t("reg.pw_norepeat") },
                  { key: "noSequence", label: t("reg.pw_nosequence") },
                ].map((r) => (
                  <div key={r.key} className={`text-xs flex items-center gap-2 ${pwChecks[r.key] ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                    <span className={`w-3.5 h-3.5 rounded-full inline-flex items-center justify-center text-[9px] ${pwChecks[r.key] ? "bg-emerald-500 text-white" : "bg-muted-foreground/30 text-transparent"}`}>✓</span>
                    {r.label}
                  </div>
                ))}
              </div>
            )}
            <Button onClick={changePassword} data-testid="pw-submit">{t("settings.update_password")}</Button>
            {loginHistory.length > 0 && (
              <div className="border-t border-border pt-4 mt-2 space-y-2" data-testid="login-history">
                <div className="font-display font-semibold text-sm flex items-center gap-2">
                  <Key size={14} /> Son Girişler
                </div>
                <div className="text-[11px] text-muted-foreground">
                  Şüpheli bir cihaz görüyorsanız şifrenizi hemen değiştirin.
                </div>
                <div className="space-y-1.5">
                  {loginHistory.map((h, idx) => {
                    const dt = new Date(h.at);
                    const when = dt.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
                    // Mask last two IPv4 octets for privacy parity with SMS body
                    let ip = h.ip || "?";
                    if (ip && ip.includes(".")) {
                      const parts = ip.split(".");
                      if (parts.length === 4) ip = `${parts[0]}.${parts[1]}.xx.xx`;
                    }
                    return (
                      <div key={idx} className="flex items-center justify-between gap-2 text-xs rounded-md bg-muted/40 px-2 py-1.5" data-testid={`login-row-${idx}`}>
                        <div>
                          <div className="font-medium">{h.device || "Bilinmeyen cihaz"}</div>
                          <div className="text-muted-foreground font-mono text-[10px]">{ip}</div>
                        </div>
                        <div className="text-muted-foreground text-[11px] whitespace-nowrap">{when}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="font-display font-bold text-lg flex items-center gap-2"><Money size={20} /> {t("settings.salary")}</div>
            <div className="text-sm">
              {t("settings.approved_salary")}: <b>{user?.salary_approved ? `${Number(user?.salary || 0).toLocaleString("tr-TR")} ₺` : t("settings.not_approved")}</b>
              {!user?.salary_approved && user?.salary_pending > 0 && (
                <div className="text-xs text-warning mt-1">{t("settings.pending_salary")}: {Number(user.salary_pending).toLocaleString("tr-TR")} ₺ — {t("settings.awaiting_hr")}</div>
              )}
            </div>
            <div><Label>{t("settings.requested_salary")}</Label><Input type="number" value={salaryReq} onChange={(e) => setSalaryReq(e.target.value)} data-testid="salary-req" /></div>
            <Button onClick={requestSalaryChange} data-testid="salary-submit">{t("settings.request_change")}</Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="font-display font-bold text-lg flex items-center gap-2"><Translate size={20} /> {t("settings.language")}</div>
            <div className="flex flex-wrap gap-2">
              <Button variant={lang === "tr" ? "default" : "outline"} onClick={() => setLang("tr")} data-testid="lang-tr">Türkçe</Button>
              <Button variant={lang === "en" ? "default" : "outline"} onClick={() => setLang("en")} data-testid="lang-en">English</Button>
              <Button variant={lang === "id" ? "default" : "outline"} onClick={() => setLang("id")} data-testid="lang-id">Bahasa Indonesia</Button>
            </div>
            <div className="text-xs text-muted-foreground">{t("settings.language_hint")}</div>
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardContent className="p-6 space-y-4">
            <div className="font-display font-bold text-lg flex items-center gap-2"><LinkIcon size={20} /> Personel Erişim Bağlantısı</div>
            <div className="text-sm text-muted-foreground">
              Personelinize bu bağlantıyı ve/veya QR kodunu paylaşın. Bağlantıya tıklayan personel doğrudan giriş sayfasına ulaşır.
              {" "}Eğer personel "site güvenli değil" uyarısı görürse: (1) telefonun tarih/saati doğru mu kontrol edin, (2) tarayıcıyı güncelleyin, (3) mobil verisiyle deneyin (bazı WiFi ağları SSL denetimi yapar), (4) tarayıcı gizli/incognito modunda açın.
            </div>
            <div className="flex flex-col md:flex-row gap-4 items-start">
              <div className="flex-1 space-y-2 w-full">
                <div className="flex gap-2">
                  <Input readOnly value={`${appUrl}/login`} data-testid="share-link" onFocus={(e) => e.target.select()} />
                  <Button variant="outline" onClick={copyLink} data-testid="copy-link"><Copy size={16} className="mr-1" />Kopyala</Button>
                </div>
                <div className="text-xs text-muted-foreground">Bu bağlantıyı e-posta, WhatsApp veya SMS ile paylaşabilirsiniz.</div>
              </div>
              {qrSrc && (
                <div className="flex flex-col items-center gap-2">
                  <div className="rounded-md border border-border bg-white p-2">
                    <img src={qrSrc} alt="QR" width={140} height={140} data-testid="share-qr" />
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><QrCode size={12} /> Kamera ile tarayın</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="font-display font-bold text-lg flex items-center gap-2"><IdentificationCard size={20} /> {t("settings.name_change")}</div>
            <div className="text-sm text-muted-foreground">
              {t("settings.name_change_desc")}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>{t("settings.new_first")}</Label><Input value={nameReq.first_name} onChange={(e) => setNameReq({...nameReq, first_name: e.target.value})} placeholder={user?.first_name} data-testid="name-req-first" /></div>
              <div><Label>{t("settings.new_last")}</Label><Input value={nameReq.last_name} onChange={(e) => setNameReq({...nameReq, last_name: e.target.value})} placeholder={user?.last_name} data-testid="name-req-last" /></div>
            </div>
            <div><Label>{t("settings.reason")}</Label><Input value={nameReq.reason} onChange={(e) => setNameReq({...nameReq, reason: e.target.value})} placeholder={lang === "en" ? "Marriage, divorce, court order..." : "Evlilik, boşanma, mahkeme kararı..."} data-testid="name-req-reason" /></div>
            <Button onClick={submitNameChange} data-testid="name-req-submit">{t("settings.submit_request")}</Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="font-display font-bold text-lg">{t("settings.security")}</div>
            <div className="flex items-center gap-3">
              <Switch checked={twoFA} onCheckedChange={setTwoFA} data-testid="2fa-toggle" />
              <Label>{t("settings.two_factor")}</Label>
            </div>
            <div>
              <Label>{t("settings.phone")}</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="settings-phone" />
            </div>
            <Button onClick={savePref} data-testid="settings-save"><ShieldCheck size={16} className="mr-1" />{t("common.save")}</Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="font-display font-bold text-lg">{t("settings.profile_photo")}</div>
            <Input type="file" accept="image/*" onChange={(e) => setPhoto(e.target.files?.[0])} data-testid="photo-input" />
            <Button variant="outline" onClick={uploadPhoto} disabled={!photo}>{t("common.upload")}</Button>
          </CardContent>
        </Card>

        {user?.role === "admin" && (
          <Card className="lg:col-span-2">
            <CardContent className="p-6 space-y-3">
              <div className="font-display font-bold text-lg flex items-center gap-2"><ImageIcon size={20} /> Giriş Ekranı Görseli</div>
              <div className="text-sm text-muted-foreground">Login sayfasında sol tarafta gösterilen görseli değiştirin (jpg/png/webp).</div>
              {currentBg && (
                <div className="rounded-md overflow-hidden border border-border" style={{ maxWidth: 480 }}>
                  <img src={currentBg} alt="mevcut" className="w-full h-40 object-cover" />
                </div>
              )}
              <Input type="file" accept="image/*" onChange={(e) => setLoginBg(e.target.files?.[0])} data-testid="login-bg-input" />
              <Button variant="outline" onClick={uploadLoginBg} disabled={!loginBg} data-testid="login-bg-upload">Yükle</Button>
            </CardContent>
          </Card>
        )}

        {user?.role === "admin" && (
          <Card className="lg:col-span-2">
            <CardContent className="p-6 space-y-3">
              <div className="font-display font-bold text-lg">Giriş Ekranı Metinleri</div>
              <div className="text-sm text-muted-foreground">Login sayfasının sol alt köşesindeki tanıtım metinlerini iki dilde düzenleyin. Kullanıcının seçtiği dile göre otomatik gösterilir.</div>

              <div className="rounded-md border border-border p-4 space-y-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Türkçe</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label>Üst etiket (Eyebrow)</Label>
                    <Input value={tagline.eyebrow || ""} onChange={(e) => setTagline({...tagline, eyebrow: e.target.value})} data-testid="tagline-eyebrow" placeholder="Colossae Hotel" />
                  </div>
                  <div>
                    <Label>Başlık</Label>
                    <Input value={tagline.title || ""} onChange={(e) => setTagline({...tagline, title: e.target.value})} data-testid="tagline-title" placeholder="Personel Yönetim Sistemi" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Alt Metin</Label>
                    <Input value={tagline.subtitle || ""} onChange={(e) => setTagline({...tagline, subtitle: e.target.value})} data-testid="tagline-subtitle" placeholder="Ekip yönetimi, izinler, bordro ve iç iletişim tek panelde." />
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-border p-4 space-y-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">English</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label>Eyebrow</Label>
                    <Input value={tagline.eyebrow_en || ""} onChange={(e) => setTagline({...tagline, eyebrow_en: e.target.value})} data-testid="tagline-eyebrow-en" placeholder="Colossae Hotel" />
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input value={tagline.title_en || ""} onChange={(e) => setTagline({...tagline, title_en: e.target.value})} data-testid="tagline-title-en" placeholder="Personnel Management System" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Subtitle</Label>
                    <Input value={tagline.subtitle_en || ""} onChange={(e) => setTagline({...tagline, subtitle_en: e.target.value})} data-testid="tagline-subtitle-en" placeholder="Team management, leaves, payroll and internal messaging in one panel." />
                  </div>
                </div>
              </div>

              <Button onClick={saveTagline} data-testid="tagline-save">Kaydet</Button>
            </CardContent>
          </Card>
        )}

        {["admin", "hr", "gm"].includes(user?.role) && (
          <Card className="lg:col-span-2">
            <CardContent className="p-6 space-y-3">
              <div className="font-display font-bold text-lg">Şirket Kaşesi</div>
              <div className="text-sm text-muted-foreground">
                Bordroya kaşe basmak için bir kaşe görseli yükleyin. Kaşeyi manuel olarak istediğiniz bordroya &quot;Bordro Onay Takibi&quot; ekranından uygulayabilirsiniz.
              </div>
              <div className="flex flex-wrap items-center gap-4">
                {stamp?.url && (
                  <div className="rounded-md bg-white border p-3">
                    <img src={stamp.url.startsWith("http") ? stamp.url : `${API}${stamp.url}`} alt="kaşe" className="h-24 object-contain" />
                    {stamp.uploaded_by_name && <div className="text-[10px] text-muted-foreground mt-1">Yükleyen: {stamp.uploaded_by_name}</div>}
                  </div>
                )}
                <div className="flex-1 min-w-[240px]">
                  <Label>Kaşe Görseli (JPG/PNG)</Label>
                  <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setStampFile(e.target.files?.[0])} data-testid="stamp-file" />
                </div>
                <Button onClick={uploadStamp} disabled={!stampFile} data-testid="stamp-upload">Yükle</Button>
              </div>
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-3">
                <Switch checked={autoStamp} onCheckedChange={toggleAutoStamp} data-testid="auto-stamp-toggle" />
                <div>
                  <div className="text-sm font-medium">Personel imzaladığında bordroya otomatik kaşe uygula</div>
                  <div className="text-xs text-muted-foreground">Açıkken: bir personel bordroyu onayladığı anda yüklü şirket kaşesi otomatik olarak imzalı sürüme basılır. Kapatınca sadece manuel &quot;Kaşe Bas&quot; ile uygulanır.</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {user?.role === "admin" && (
          <Card className="lg:col-span-2">
            <CardContent className="p-6 space-y-3">
              <div className="font-display font-bold text-lg">Veri Yedekleme</div>
              <div className="text-sm text-muted-foreground">
                Manuel yedek alın. Otomatik yedekleme her gece <b>23:59</b>'da <b>.xlsx</b> formatında sunucuda oluşturulur.
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => backup("xlsx")} data-testid="backup-xlsx-btn"><Download size={16} className="mr-1" />Excel (.xlsx) İndir</Button>
                <Button variant="outline" onClick={() => backup("zip")} data-testid="backup-btn"><Download size={16} className="mr-1" />ZIP (JSON) İndir</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
